import styled, { css } from 'styled-components';

/**
 * The flyout's inner content surface — sits inside the sliding shell from
 * `NodeLibraryFlyout.styled` so this component focuses purely on layout
 * (header / search / chips / list / scroll).
 */
export const SidebarRoot = styled.aside`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'};
  overflow: hidden;
  isolation: isolate;
`;

export const FlyoutHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 12px 8px 16px;
  flex-shrink: 0;
`;

export const FlyoutTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
  color: ${({ theme: { colors = {} } }) =>
    colors.primaryEmphasis ?? colors.primary ?? '#CFC9F5'};
`;

export const SearchRow = styled.div`
  padding: 4px 16px 12px;
  flex-shrink: 0;
`;

export const CategoryRow = styled.div`
  position: relative;
  padding: 0 16px 8px;
  flex-shrink: 0;
`;

/**
 * Inline anchor for the category chip + its dropdown. Inline-block keeps
 * the anchor sized to its trigger, and `position: relative` lets the
 * absolutely-positioned ChipMenu (`CategoryMenuPosition`) latch on to it.
 */
export const CategoryAnchor = styled.div`
  position: relative;
  display: inline-block;
`;

interface CategoryChipProps {
  $isOpen?: boolean;
}

/**
 * Filter chip that opens the category ChipMenu. Same visual vocabulary as
 * the prototype's `.icon-chip` — pill-shaped, icon + label, primary-tinted
 * while the menu is open.
 */
export const CategoryChipTrigger = styled.button<CategoryChipProps>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 30px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.full ?? '999px'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  background: transparent;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.05px;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  & > svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  }

  ${({ $isOpen, theme: { colors = {} } }) =>
    $isOpen &&
    css`
      background: ${colors.surfaceContainerHigh ?? '#1F2030'};
      color: ${colors.onSurface ?? '#E6E6F2'};
      border-color: ${colors.outline ?? colors.outlineVariant ?? '#5F5E73'};
    `}
`;

export const CategoryChipLabel = styled.span`
  display: inline-block;
  max-width: none;
  white-space: nowrap;
`;

/**
 * The ChipMenu floats absolutely below the trigger; this wrapper places it
 * just under the chip without participating in the flow.
 */
export const CategoryMenuPosition = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 5;
`;

export const SectionList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme: { colors = {} } }) =>
      colors.outlineVariant ?? '#25263A'};
    border-radius: 3px;
  }
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Section header — `[●] TASKS [───────────]`. The dot, title, and the
 * trailing gradient rule all sit on a single flex row, mirroring the
 * prototype's `.ops-group__title` (dot/title/line as `::before`, text,
 * `::after` pseudos respectively). We model them as three siblings
 * here for clarity and so the rule can grow flexibly via `flex: 1`.
 */
export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 4px 8px;
`;

export const SectionDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme: { colors = {} } }) =>
    colors.primaryEmphasis ?? colors.primary ?? '#CFCAF2'};
  box-shadow: 0 0 10px
    ${({ theme: { colors = {} } }) =>
      colors.primaryEmphasis
        ? `${colors.primaryEmphasis}b3`
        : 'rgba(207, 202, 242, 0.7)'};
  flex-shrink: 0;
`;

export const SectionTitle = styled.div`
  font-family: 'Source Code Pro', Menlo, Monaco, Consolas, 'Courier New',
    monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  flex-shrink: 0;
  white-space: nowrap;
`;

/**
 * Trailing gradient rule that fills the remaining width to the right
 * of the section title — fades from `outline-variant` to transparent,
 * matching the prototype's `.ops-group__title::after`.
 */
export const SectionRule = styled.span`
  flex: 1;
  height: 1px;
  background: linear-gradient(
    90deg,
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'},
    transparent
  );
`;

export const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/**
 * Pill-shaped library row — direct port of the prototype's
 * `.ops-panel .ops-row`:
 *   - 30x30 dark-gradient icon chip on the left, label on the right
 *     (no subtitle in the flyout variant — `.ops-row__sub` is hidden)
 *   - `surface-container-low` background, AT REST AND ON HOVER —
 *     hover only adds a 1px inset outline-line via box-shadow
 *   - pill border-radius (999px)
 */
export const ItemRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 30px 1fr;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 9px;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerLow ?? '#13141E'};
  border: 0;
  border-radius: 999px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  text-align: left;
  cursor: grab;
  user-select: none;
  box-shadow: inset 0 0 0 1px transparent;
  transition: box-shadow 140ms ease;

  &:hover {
    box-shadow: inset 0 0 0 1px
      ${({ theme: { colors = {} } }) => colors.outline ?? '#676680'};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: -2px;
  }

  &:active {
    cursor: grabbing;
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
  }
`;

/**
 * 30x30 dark-gradient icon chip — matches the prototype's
 * `.ops-panel .ops-row__icon` (which OVERRIDES the lavender base rule
 * inside `.ops-panel`). At rest: dark vertical gradient, hairline
 * outline, soft inset highlight. On hover: outline drops to transparent
 * and a soft drop-shadow lifts the chip.
 */
export const ItemIcon = styled.div`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #343548, #28293a);
  border: 1px solid #404058;
  color: #d4d0f2;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.04) inset;
  transition: box-shadow 160ms ease, border-color 160ms ease;

  ${ItemRow}:hover & {
    border-color: transparent;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.06) inset,
      0 4px 14px rgba(0, 0, 0, 0.85);
  }

  & > svg {
    width: 18px;
    height: 18px;
  }
`;

export const ItemBody = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  line-height: 1.25;
`;

export const ItemLabel = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.1px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

